import React, { useCallback, useMemo } from 'react';
import {
  Alert,
  Linking,
  Platform,
  StyleSheet,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import Animated, { type ScrollHandlerProcessed } from 'react-native-reanimated';
import { WebView } from 'react-native-webview';
import type { ShouldStartLoadRequest } from 'react-native-webview/lib/WebViewTypes';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { cmsPageHtmlToDisplayText } from '../../shared/utils/htmlToPlainText';

export interface CmsPagesApiHtmlScrollProps {
  /** Raw HTML from GET pages (CMS) `content` / `a_content`. */
  html: string;
  scrollViewStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Optional text color/size hints applied to the HTML document body. */
  textStyle?: StyleProp<TextStyle>;
  selectable?: boolean;
  /** Kept for API compatibility; WebView owns scrolling so this is unused. */
  onScroll?: ScrollHandlerProcessed;
  scrollEventThrottle?: number;
}

function flattenTextStyle(style: StyleProp<TextStyle> | undefined): TextStyle {
  return (StyleSheet.flatten(style) ?? {}) as TextStyle;
}

function buildCmsPagesHtmlDocument(
  body_html: string,
  colors: ThemeColors,
  is_rtl: boolean,
  text_style: TextStyle,
): string {
  const text = colors.text ?? '#111111';
  const text_secondary =
    typeof text_style.color === 'string' ? text_style.color : (colors.textSecondary ?? '#666666');
  const primary = colors.primary ?? '#007AFF';
  const surface = colors.surfaceLight ?? '#F5F5F5';
  const border = colors.border ?? '#E0E0E0';
  const font_size = typeof text_style.fontSize === 'number' ? text_style.fontSize : 14;
  const line_height =
    typeof text_style.lineHeight === 'number' ? text_style.lineHeight : Math.round(font_size * 1.55);

  return `<!DOCTYPE html>
<html lang="${is_rtl ? 'ar' : 'en'}" dir="${is_rtl ? 'rtl' : 'ltr'}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      font-size: ${font_size}px;
      line-height: ${line_height}px;
      color: ${text_secondary};
      background: transparent;
      -webkit-text-size-adjust: 100%;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    h1, h2, h3, h4, h5, h6 {
      color: ${text};
      font-weight: 700;
      margin: 16px 0 8px;
    }
    h1:first-child, h2:first-child, h3:first-child, p:first-child { margin-top: 0; }
    p { margin: 0 0 12px; }
    strong, b { color: ${text}; font-weight: 700; }
    em, i { font-style: italic; }
    a { color: ${primary}; word-break: break-word; }
    ul, ol { margin: 0 0 12px; padding-inline-start: 20px; }
    li { margin-bottom: 8px; }
    br { line-height: ${line_height}px; }
    blockquote {
      margin: 0 0 16px;
      padding: 12px 14px;
      border-radius: 14px;
      background: ${surface};
      border: 1px solid ${border};
    }
  </style>
</head>
<body>${body_html}</body>
</html>`;
}

/**
 * Displays CMS pages API HTML with formatting preserved (WebView), matching health-info CMS rendering.
 */
export function CmsPagesApiHtmlScroll({
  html,
  scrollViewStyle,
  contentContainerStyle,
  textStyle,
}: CmsPagesApiHtmlScrollProps): React.ReactElement | null {
  const colors = useThemeColors() ?? appTheme.colors;
  const { t } = useTranslation();
  const rtl = useRtl();
  const flat_text = useMemo(() => flattenTextStyle(textStyle), [textStyle]);

  const display_text = cmsPageHtmlToDisplayText(html);
  const document_html = useMemo(
    () => (display_text ? buildCmsPagesHtmlDocument(html, colors, rtl.isRTL, flat_text) : ''),
    [colors, display_text, flat_text, html, rtl.isRTL],
  );

  const open_external_url = useCallback(
    async (url: string) => {
      try {
        const supported = await Linking.canOpenURL(url);
        if (supported) {
          await Linking.openURL(url);
        } else {
          Alert.alert(t('common.error'), t('common.error'));
        }
      } catch {
        Alert.alert(t('common.error'), t('common.error'));
      }
    },
    [t],
  );

  const on_should_start_load_with_request = useCallback(
    (request: ShouldStartLoadRequest) => {
      const url = request.url ?? '';
      if (url === 'about:blank' || url.startsWith('data:')) {
        return true;
      }
      if (/^https?:\/\//i.test(url)) {
        void open_external_url(url);
        return false;
      }
      return false;
    },
    [open_external_url],
  );

  if (!display_text || !document_html) {
    return null;
  }

  return (
    <Animated.View style={[styles.wrap, scrollViewStyle, contentContainerStyle]}>
      <WebView
        originWhitelist={['*']}
        source={{ html: document_html, baseUrl: 'about:blank' }}
        style={styles.webview}
        scrollEnabled
        nestedScrollEnabled
        showsVerticalScrollIndicator={Platform.OS === 'android'}
        setSupportMultipleWindows={false}
        onShouldStartLoadWithRequest={on_should_start_load_with_request}
        javaScriptEnabled={false}
        domStorageEnabled={false}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    minHeight: 0,
  },
  webview: {
    flex: 1,
    backgroundColor: 'transparent',
  },
});
